import { useMutation } from '@apollo/client/react';
import {
  computeBookingPrice,
  ControlledNameField,
  CouponInsert,
  ServicesSelection,
  sortServicesForSelection,
} from '@drycut/booking-components';
import { ControlledPhoneField } from '@drycut/booking-components';
import { PaymentMethod } from '@drycut/controlled-components';
import {
  type BasicCouponFragment,
  type BasicServiceFragment,
  CreateBookingDocument,
  CreateBookingTransactionDocument,
  GetServiceExecutionsDocument,
  type RecordError,
} from '@drycut/graphql';
import {
  ActionButtons,
  BigDisplayField,
  Button,
  CheckBox,
  Modal,
  Para,
  Stack,
} from '@drycut/ui-components';
import { Transition } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { NO_PHONE_NUMBER } from '../constants';
import { useStoreContext } from '../useStoreContext';
import BookingError from './BookingError';

type Props = {
  className?: string;
};

type ManualBooking = {
  couponCode: string;
  name: string;
  phone: string;
};

export function StylistManualBooking({ className }: Props): React.ReactElement {
  const { store } = useStoreContext();
  const [error, showError] = useState<null | RecordError>(null);
  const { value: open, toggle: toggleOpen } = useBoolean(false);
  const { value: hasPhone, setTrue: needsPhone, toggle: togglePhone } = useBoolean(true);
  const [coupon, setCoupon] = useState<BasicCouponFragment | undefined>();
  const [selectedServices, setSelectedServices] = useState<Array<BasicServiceFragment>>([]);
  const formMethods = useForm<ManualBooking>({
    defaultValues: {
      couponCode: '',
      name: '',
      phone: '',
    },
  });

  function handleOpen() {
    formMethods.reset();
    setCoupon(undefined);
    // Default to the first haircut service so there is always a valid selection.
    const services = store?.services ?? [];
    setSelectedServices(services.length ? [sortServicesForSelection(services)[0]] : []);
    showError(null);
    needsPhone();
    toggleOpen();
  }

  const computedPrice = computeBookingPrice({
    services: selectedServices,
    coupon,
  });

  const [createPayment, { loading: creatingPayment }] = useMutation(
    CreateBookingTransactionDocument,
  );
  const [createBooking, { loading: creatingBooking }] = useMutation(CreateBookingDocument);

  const onSubmit = async (data: ManualBooking) => {
    const paymentResult = await createPayment({
      variables: {
        input: {
          amount: computedPrice,
          paymentMethod: PaymentMethod.Direct,
          data: {
            storeSlug: store?.slug || 'NOSTORE',
            phoneNumber: data.phone,
            manual: true,
            couponCode: data.couponCode,
            customerName: data.name,
            serviceIds: selectedServices.map((service) => service.id),
          },
        },
      },
    });

    const payload = paymentResult?.data?.createBookingTransaction;

    if (payload?.paymentTransaction?.reference) {
      const bookingResult = await createBooking({
        variables: {
          input: {
            transactionId: payload.paymentTransaction.id,
          },
        },
        refetchQueries: [GetServiceExecutionsDocument],
      });

      const result = bookingResult.data?.createBooking;

      if (result?.booking) {
        toggleOpen();
      } else {
        const error = result?.errors?.[0];
        if (error) {
          showError(error);
        }
      }
    } else {
      const error = payload?.errors?.[0];
      if (error) {
        showError(error);
      }
    }
  };

  const loading = creatingBooking || creatingPayment;

  return (
    <>
      <Button fill="outlined" size="small" className={className} onClick={handleOpen}>
        <Stack direction="row" gap={1}>
          <PlusIcon className="w-5 h-5" />
          <Para as="span" className="font-aribaubold hidden lg:block">
            Man. Kunde
          </Para>
        </Stack>
      </Button>

      <Modal open={open} onClose={toggleOpen} title="Man. Kunde" overlayClose={false} closeButton>
        <Stack
          as="form"
          gap={4}
          className="px-10"
          noValidate
          onSubmit={formMethods.handleSubmit(onSubmit)}
        >
          <FormProvider {...formMethods}>
            <Stack as="fieldset" gap={4} disabled={loading} className="align-center">
              <BigDisplayField content={computedPrice} suffix="CHF" />

              {(store?.services?.length ?? 0) > 1 && (
                <ServicesSelection
                  services={store?.services ?? []}
                  selectedServices={selectedServices}
                  onServicesSelect={setSelectedServices}
                />
              )}

              <ControlledNameField autoComplete="off" />
              <Transition
                show={hasPhone}
                enter="ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
                beforeEnter={() => formMethods.setValue('phone', '')}
                afterLeave={() => formMethods.setValue('phone', NO_PHONE_NUMBER)}
              >
                <div>
                  <ControlledPhoneField autoComplete="off" />
                </div>
              </Transition>

              <CheckBox
                name="sendSms"
                label="Möchte Nummer nicht angeben"
                onChange={togglePhone}
                checked={!hasPhone}
                justify="center"
              />
            </Stack>

            <CouponInsert onValidate={setCoupon} disabled={loading} />

            {error && <BookingError error={error} />}

            <ActionButtons>
              <Button type="submit" loading={loading}>
                Weiter
              </Button>

              <Button type="button" fill="outlined" onClick={toggleOpen} disabled={loading}>
                Cancel
              </Button>
            </ActionButtons>
          </FormProvider>
        </Stack>
      </Modal>
    </>
  );
}
